import * as XLSX from 'xlsx';
import Papa from 'papaparse';

/**
 * Shared CSV / Excel export for the SuperAdmin list views.
 *
 * `columns` is a list of { header, value } where `value(row)` returns what
 * goes in the cell. Dates, booleans, arrays and nullish values are
 * normalized so every list exports the same way.
 */

const pad = (n) => String(n).padStart(2, '0');

export const formatExportDate = (d, withTime = true) => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const day = `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
  return withTime ? `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
};

const normalizeCell = (v) => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return formatExportDate(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined && x !== '').join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return v;
};

const toSheetRows = (rows, columns) =>
  rows.map((row) => {
    const out = {};
    columns.forEach((c) => {
      let v;
      try {
        v = c.value(row);
      } catch {
        v = '';
      }
      out[c.header] = normalizeCell(v);
    });
    return out;
  });

const stampedName = (fileName) => `${fileName}-${new Date().toISOString().split('T')[0]}`;

/** Download `rows` as a .csv or .xlsx file. */
export const downloadRows = (rows, columns, fileName, format = 'csv') => {
  const data = toSheetRows(rows, columns);
  const headers = columns.map((c) => c.header);
  const name = stampedName(fileName);

  if (format === 'xlsx') {
    const sheet = XLSX.utils.json_to_sheet(data, { header: headers });
    // Size each column to its longest value (capped) so the sheet is readable on open.
    sheet['!cols'] = headers.map((h) => ({
      wch: Math.min(50, Math.max(h.length, ...data.map((r) => String(r[h] ?? '').length)) + 2),
    }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Data');
    XLSX.writeFile(book, `${name}.xlsx`);
    return;
  }

  // BOM so Excel opens UTF-8 (₹, non-Latin names) correctly.
  const csv = '﻿' + Papa.unparse({ fields: headers, data: data.map((r) => headers.map((h) => r[h])) });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${name}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Fetch every page of a paginated SuperAdmin list so an export contains all
 * rows matching the current filters, not just the page on screen.
 * `fetcher(params)` is the axios call (e.g. superAdminAPI.getOrganizations);
 * responses are expected as { data: [...], pagination: { totalPages } } —
 * pass `pick(body)` → { rows, totalPages } for any other shape.
 */
const defaultPick = (body) => ({
  rows: Array.isArray(body.data) ? body.data : [],
  totalPages: body.pagination?.totalPages,
});

export const fetchAllPages = async (fetcher, params = {}, { limit = 500, maxPages = 100, pick = defaultPick } = {}) => {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetcher({ ...params, page, limit });
    const picked = pick(res?.data || {});
    const rows = picked.rows || [];
    all.push(...rows);
    totalPages = picked.totalPages || 1;
    page += 1;
    if (rows.length === 0) break;
  } while (page <= totalPages && page <= maxPages);
  return all;
};

/** Local calendar dates ("YYYY-MM-DD") → ISO day boundaries for the API's from/to params. */
export const dateRangeParams = (from, to) => {
  const out = {};
  if (from) {
    const [y, m, d] = from.split('-').map(Number);
    out.from = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
  }
  if (to) {
    const [y, m, d] = to.split('-').map(Number);
    out.to = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
  }
  return out;
};

/** Client-side check for lists that are loaded in full (no server pagination). */
export const inDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;
  const t = new Date(value).getTime();
  const range = dateRangeParams(from, to);
  if (range.from && t < new Date(range.from).getTime()) return false;
  if (range.to && t > new Date(range.to).getTime()) return false;
  return true;
};

/** Case-insensitive match of `term` against any of the given values. */
export const matchesSearch = (term, ...values) => {
  if (!term) return true;
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return values.some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q));
};
